import { Loader2, X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { PersonPicker } from "@/components/person-picker";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { type Person, type Team, useCreateTeam, useSetTeamMembers, useUpdateTeam } from "@/lib/api";
import { apiErrorMessage, inlineFailure } from "@/lib/api-errors";
import { formatNumber } from "@/lib/format";
import { normalEmail } from "@/lib/people";

import { AccessGrid } from "./access-grid";
import {
  adminsMembership,
  type AppGrant,
  type GrantRow,
  isAdminsTeam,
  knownGrants,
  memberLabel,
  membersUpdate,
  peopleByEmail,
  teamDraft,
  teamUnits,
  teamUpdate,
  withMember,
} from "./utils";

/** The longest name and description the server takes. */
const NAME_MAX = 60;
const DESCRIPTION_MAX = 200;

interface TeamEditorProps {
  /** The team to edit, or null for a new one. */
  team: Team | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rows: readonly GrantRow[];
  /** The apps a team can be given; the default team gets none. */
  apps: readonly AppGrant[];
  people: readonly Person[];
  domains: readonly string[];
}

/**
 * A team's name, description, access and members in one dialog; the default
 * team (everyone in no team) shows its access alone. The Admins team shows
 * "Alle toegang" instead of the grid, its members by role (not removable
 * here) and its extra members, and no name or description: the dashboard
 * names it. Save sends only what changed.
 */
export function TeamEditor({ team, open, onOpenChange, ...rest }: TeamEditorProps) {
  const { t } = useTranslation();
  const isDefault = team?.is_default ?? false;
  const isAdmins = isAdminsTeam(team);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {isDefault
              ? t("teams.defaultTeam")
              : isAdmins
                ? t("teams.adminsTeam")
                : team
                  ? t("teams.editor.editTitle")
                  : t("teams.editor.newTitle")}
          </DialogTitle>
          {isDefault ? (
            <DialogDescription>{t("teams.editor.defaultDescription")}</DialogDescription>
          ) : isAdmins ? (
            <DialogDescription>{t("teams.editor.adminsDescription")}</DialogDescription>
          ) : (
            <DialogDescription className="sr-only">{t("teams.explainTeams")}</DialogDescription>
          )}
        </DialogHeader>
        {/* Mounted while open only, so every opening starts from the team as saved. */}
        <TeamForm
          key={team?.id ?? "new"}
          team={team}
          onClose={() => onOpenChange(false)}
          {...rest}
        />
      </DialogContent>
    </Dialog>
  );
}

interface TeamFormProps extends Omit<TeamEditorProps, "open" | "onOpenChange"> {
  onClose: () => void;
}

function TeamForm({ team, rows, apps, people, domains, onClose }: TeamFormProps) {
  const { t } = useTranslation();
  const createTeam = useCreateTeam();
  const updateTeam = useUpdateTeam();
  const setMembers = useSetTeamMembers();
  const [draft, setDraft] = useState(() => teamDraft(team));
  // A new team once created: a retry after a failed member update must not create it again.
  const [saved, setSaved] = useState<Team | null>(team);
  // The failed attempt, worded at render so a language switch re-words it.
  const [failure, setFailure] = useState<{ error: unknown } | null>(null);

  const isDefault = team?.is_default ?? false;
  const isAdmins = isAdminsTeam(team);
  const pending = createTeam.isPending || updateTeam.isPending || setMembers.isPending;
  const named = isDefault || isAdmins || draft.name.trim() !== "";
  // What a save may send: the grid's parts, and the apps unless it is the default team.
  const units = teamUnits(rows, apps, isDefault);
  const changed = saved
    ? teamUpdate(saved, draft, units) !== null || membersUpdate(saved, draft) !== null
    : true;
  const known = peopleByEmail(people);
  // On the Admins team the admins are members by role: never offered as extra members.
  const membership = adminsMembership(isAdmins ? (team?.role_members ?? []) : [], draft.members);
  const admins = new Set(membership.byRole);
  const shownMembers = membership.extras;
  const removeMember = (email: string) =>
    setDraft((d) => ({ ...d, members: withMember(d.members, email, false) }));

  const save = async () => {
    setFailure(null);
    try {
      let current = saved;
      if (!current) {
        current =
          (await createTeam.mutateAsync({
            name: draft.name.trim(),
            description: draft.description.trim() || null,
            grants: knownGrants(draft.grants, units),
          })) ?? null;
        if (!current) {
          throw new Error("The server returned no team");
        }
        setSaved(current);
      } else {
        const change = teamUpdate(current, draft, units);
        if (change) {
          current = (await updateTeam.mutateAsync({ teamId: current.id, change })) ?? current;
          setSaved(current);
        }
      }
      const members = membersUpdate(current, draft);
      if (members) {
        await setMembers.mutateAsync({ teamId: current.id, members });
      }
      onClose();
    } catch (err) {
      setFailure(inlineFailure({ error: err }));
    }
  };
  const error = failure && apiErrorMessage(failure.error, "saveTeam");

  return (
    <>
      <div className="space-y-5">
        {!isDefault && !isAdmins && (
          <div className="space-y-2">
            <Input
              aria-label={t("teams.editor.name")}
              placeholder={t("teams.editor.name")}
              value={draft.name}
              maxLength={NAME_MAX}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              autoFocus={!team}
            />
            <Input
              aria-label={t("teams.editor.description")}
              placeholder={t("teams.editor.description")}
              value={draft.description}
              maxLength={DESCRIPTION_MAX}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })}
            />
          </div>
        )}
        {isAdmins ? (
          <AllAccess />
        ) : (
          <AccessGrid
            rows={rows}
            apps={isDefault ? [] : apps}
            grants={draft.grants}
            onChange={(grants) => setDraft({ ...draft, grants })}
            disabled={pending}
          />
        )}
        {isAdmins && (
          <RoleMembers
            emails={membership.byRole}
            both={membership.both}
            known={known}
            onRemoveExtra={removeMember}
            disabled={pending}
          />
        )}
        {!isDefault && (
          <section className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-foreground text-sm font-medium">
                {isAdmins ? t("teams.editor.extraMembers") : t("teams.editor.members")}{" "}
                <span className="text-muted font-normal">
                  ({formatNumber(shownMembers.length)})
                </span>
              </h3>
              <PersonPicker
                people={people.filter((person) => !admins.has(normalEmail(person.email)))}
                label={t("teams.editor.addPeople")}
                multiple
                chosen={shownMembers}
                onPick={(email, on) => {
                  // An admin typed by email is a member by role already.
                  if (!admins.has(normalEmail(email))) {
                    setDraft((d) => ({ ...d, members: withMember(d.members, email, on) }));
                  }
                }}
                addByEmail={{ domains }}
                className="w-full sm:w-56"
              />
            </div>
            <p className="text-muted text-xs">
              {isAdmins && <span className="block">{t("teams.editor.extraMembersHint")}</span>}
              {t("teams.editor.membersHint")}
            </p>
            {shownMembers.length === 0 ? (
              <p className="text-muted text-sm">{t("teams.editor.noMembers")}</p>
            ) : (
              <ul className="flex flex-wrap gap-1.5 pointer-coarse:gap-y-6">
                {shownMembers.map((email) => {
                  const name = memberLabel(email, known);
                  return (
                    <li
                      key={email}
                      title={email}
                      className="bg-accent-light text-primary inline-flex max-w-full items-center gap-1 rounded-sm py-0.5 pr-0.5 pl-2 text-xs font-medium"
                    >
                      <span className="truncate">{name}</span>
                      <button
                        type="button"
                        onClick={() => removeMember(email)}
                        aria-label={t("teams.editor.removeMember", { person: name })}
                        className="hover:bg-primary hover:text-primary-foreground focus-visible:ring-ring tap-target rounded-sm p-0.5 focus-visible:ring-2 focus-visible:outline-none pointer-coarse:relative"
                      >
                        <X aria-hidden="true" className="size-3" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        )}
        {error && (
          <p role="alert" className="text-destructive text-sm">
            {error}
          </p>
        )}
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          {t("common.cancel")}
        </Button>
        <Button onClick={() => void save()} disabled={pending || !named || !changed}>
          {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : t("common.save")}
        </Button>
      </DialogFooter>
    </>
  );
}

/** The Admins team's access, instead of the grid: everything, computed by the server. */
function AllAccess() {
  const { t } = useTranslation();
  return (
    <div className="rounded-lg border px-3 py-2 text-sm" data-block="all-access">
      <p className="text-foreground font-medium">{t("teams.allAccess")}</p>
      <p className="text-muted text-xs">{t("teams.editor.allAccessDetail")}</p>
    </div>
  );
}

interface RoleMembersProps {
  /** The dashboard admins, lower case, sorted. */
  emails: readonly string[];
  /** Admins also stored as an extra member (older data): marked as both, with a remove button. */
  both?: ReadonlySet<string>;
  known: ReadonlyMap<string, Person>;
  /** Removes an admin's stored extra membership; they stay a member by role. */
  onRemoveExtra?: (email: string) => void;
  disabled?: boolean;
}

/**
 * The Admins team's members by role: every dashboard admin, by name (else
 * email), marked and without a remove button: their role on the Mensen tab
 * decides it. An admin also stored as an extra member (older data) is shown
 * once, marked as both, and its button removes the extra row only.
 */
export function RoleMembers({
  emails,
  both = new Set(),
  known,
  onRemoveExtra,
  disabled = false,
}: RoleMembersProps) {
  const { t } = useTranslation();
  return (
    <section className="space-y-2" data-block="role-members">
      <h3 className="text-foreground text-sm font-medium">
        {t("teams.editor.roleMembers")}{" "}
        <span className="text-muted font-normal">({formatNumber(emails.length)})</span>
      </h3>
      <p className="text-muted text-xs">{t("teams.editor.roleMembersHint")}</p>
      {emails.length > 0 && (
        <ul className="flex flex-wrap gap-1.5">
          {emails.map((email) => {
            const name = memberLabel(email, known);
            const extra = both.has(email);
            return (
              <li
                key={email}
                data-role-member={email}
                className="bg-muted/15 text-foreground inline-flex max-w-full items-center gap-1 rounded-sm py-0.5 pr-0.5 pl-2 text-xs font-medium"
              >
                <span className="truncate">{name}</span>
                <span className="text-muted pr-1.5 font-normal">
                  ({extra ? t("teams.byRoleAndExtra") : t("teams.byRole")})
                </span>
                {extra && onRemoveExtra && (
                  <button
                    type="button"
                    onClick={() => onRemoveExtra(email)}
                    disabled={disabled}
                    aria-label={t("teams.editor.removeExtra", { person: name })}
                    className="hover:bg-primary hover:text-primary-foreground focus-visible:ring-ring tap-target rounded-sm p-0.5 focus-visible:ring-2 focus-visible:outline-none pointer-coarse:relative"
                  >
                    <X aria-hidden="true" className="size-3" />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
